/**
 * The KPIs this company's meters actually report against.
 *
 * A KPI here is not what a KPI is on Carbon Footprinting. There it is a line of
 * a scope's emissions; here it is the thing a meter is mapped to — Borewell,
 * ETP, Final Treated (STP + ETP) — carrying the module, category and
 * sub-category that address it in a request. Comparing two of those is the
 * meters question, and it needs the meters' own vocabulary.
 *
 * The catalogue is read from the meters rather than from the KPI endpoint, so
 * it only ever offers KPIs some meter is mapped to. A company cannot pick a
 * KPI nothing reports against and be shown two empty charts.
 */

/**
 * The distinct KPIs the meters are mapped to.
 *
 * Mappings from every financial year are folded together. Restricting to the
 * selected year would empty the list whenever the reader switched years, and a
 * KPI that reported in one year and not another is a comparison worth making
 * rather than an option worth hiding.
 *
 * @param {Array<object>} meters - Meters as returned by the meters API.
 * @returns {Array<{id: string, title: string, module: string, category: string,
 *   subCategory: string}>} KPI descriptors, alphabetical.
 */
export const kpiCatalogue = (meters = []) => {
  const found = new Map();

  (Array.isArray(meters) ? meters : []).forEach((meter) => {
    (meter?.financialYearKpis || []).forEach((mapping) => {
      const kpi = mapping?.kpi;
      if (!kpi?.kpi || found.has(kpi.kpi)) return;

      found.set(kpi.kpi, {
        id: kpi.kpi,
        title: kpi.kpi,
        module: kpi.module,
        category: kpi.category,
        subCategory: kpi.subCategory,
      });
    });
  });

  return Array.from(found.values()).sort((a, b) => a.title.localeCompare(b.title));
};

/**
 * The unit a set of widget rows is in.
 *
 * KPIs are not all volumes — a flow KPI reports m³/h — so the unit is read
 * from what came back rather than assumed. Nothing reported means nothing to
 * label, and the fallback is the unit the water plant is overwhelmingly in.
 *
 * @param {Array<object>} rows - Widget rows.
 * @returns {string} The unit.
 */
export const unitOf = (rows = []) =>
  (Array.isArray(rows) ? rows : []).find((row) => row?.unit)?.unit || 'm³';

/**
 * The meters reporting against one KPI, for a financial year.
 *
 * Kavana's review asked what happens when several meters share a KPI, and on
 * this fleet several do — Shibra has five, Awanthi 200 ppm has three. A
 * comparison that names only the KPI silently reads their sum, which is right
 * for a total and wrong for "is the inlet tracking the outlet".
 *
 * @param {Array<object>} meters - Meters as the list endpoint returns them.
 * @param {string} kpi - KPI name to match.
 * @param {string} financialYear - Year whose mapping decides the match.
 * @returns {Array<{value: string, label: string}>} Meters, ready for a picker.
 */
export const metersForKpi = (meters = [], kpi, financialYear) =>
  (Array.isArray(meters) ? meters : [])
    .filter((meter) =>
      (meter.financialYearKpis || []).some(
        (mapping) =>
          mapping.financialYear === financialYear && mapping.kpi?.kpi === kpi
      )
    )
    .map((meter) => ({
      value: meter.meterId,
      label: meter.meterName || meter.meterId,
    }));

/**
 * Every meter, named, for a picker.
 *
 * The comparison names meters rather than KPIs. A reader of this page thinks
 * in "32 - SPORTS CLUB", not in "Sports Club the KPI" — and on the review call
 * the KPI was the one idea that had to be explained. Naming the meter also
 * removes the hidden aggregate: several meters can share a KPI, and choosing
 * the KPI silently read their sum.
 *
 * @param {Array<object>} meters - Meters as the list endpoint returns them.
 * @param {string} financialYear - Year a meter must be mapped in to appear.
 * @returns {Array<{value: string, label: string}>} Meters, by name.
 */
export const meterOptions = (meters = [], financialYear) =>
  (Array.isArray(meters) ? meters : [])
    .filter((meter) =>
      (meter.financialYearKpis || []).some(
        (mapping) => !financialYear || mapping.financialYear === financialYear
      )
    )
    .map((meter) => ({
      value: meter.meterId,
      label: meter.meterName || meter.meterId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

/**
 * The KPI a meter is mapped to in a year, so a request can still address it.
 *
 * The widget endpoint filters by category, sub category and KPI; naming a
 * meter narrows it further but does not replace them.
 *
 * @param {Array<object>} meters - Meters as the list endpoint returns them.
 * @param {string} meterId - The meter chosen.
 * @param {string} financialYear - Year whose mapping to read.
 * @returns {object|null} The KPI descriptor, or null when unmapped.
 */
export const kpiOfMeter = (meters = [], meterId, financialYear) => {
  const meter = (Array.isArray(meters) ? meters : []).find(
    (m) => m.meterId === meterId
  );
  const mapping = (meter?.financialYearKpis || []).find(
    (m) => m.financialYear === financialYear
  );

  return mapping?.kpi
    ? {
        id: mapping.kpi.kpi,
        title: mapping.kpi.kpi,
        module: mapping.kpi.module,
        category: mapping.kpi.category,
        subCategory: mapping.kpi.subCategory,
      }
    : null;
};
